import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Save, ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";

const Edit = () => {
  const navigate = useNavigate();
  const { slug } = useParams();
  const { user } = useAuth();
  const [macro, setMacro] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tags, setTags] = useState<any[]>([]);

  // Form fields
  const [name, setName] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [osSupport, setOsSupport] = useState<string[]>([]);
  const [excelVersions, setExcelVersions] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<number[]>([]);

  useEffect(() => {
    if (!user) {
      navigate("/auth");
      return;
    }
    fetchMacro(user.id);
    fetchTags();
  }, [navigate, slug, user]);

  const fetchTags = async () => {
    const { data, error } = await (supabase as any)
      .from('tags')
      .select('*')
      .order('name') as any;
    
    if (!error && data) {
      setTags(data);
    }
  };

  const fetchMacro = async (userId: string) => {
    try {
      setLoading(true);
      const { data: macroData, error: macroError } = await supabase
        .from('macros')
        .select(`
          *,
          macro_tags (tag_id)
        `)
        .eq('slug', slug)
        .eq('owner_id', userId)
        .single();

      if (macroError) throw macroError;

      if (!macroData) {
        toast.error("Macro not found or you don't have permission to edit it");
        navigate("/dashboard");
        return;
      }

      setMacro(macroData);
      setName(macroData.name);
      setShortDesc(macroData.short_desc || "");
      setOsSupport(Array.isArray(macroData.os_support) ? macroData.os_support.filter((v): v is string => typeof v === 'string') : []);
      setExcelVersions(Array.isArray(macroData.excel_versions) ? macroData.excel_versions.filter((v): v is string => typeof v === 'string') : []);
      setSelectedTags(macroData.macro_tags?.map((mt: any) => mt.tag_id) || []);
    } catch (error: any) {
      console.error('Error fetching macro:', error);
      toast.error(error.message || "Failed to load macro");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      // Update macro
      const { error: updateError } = await (supabase as any)
        .from('macros')
        .update({
          name,
          short_desc: shortDesc,
          os_support: osSupport,
          excel_versions: excelVersions,
          updated_at: new Date().toISOString(),
        })
        .eq('id', macro.id) as any;

      if (updateError) throw updateError;

      // Update tags - delete old ones and insert new ones
      const { error: deleteTagsError } = await (supabase as any)
        .from('macro_tags')
        .delete()
        .eq('macro_id', macro.id) as any;

      if (deleteTagsError) throw deleteTagsError;

      if (selectedTags.length > 0) {
        const tagData = selectedTags.map(tagId => ({
          macro_id: macro.id,
          tag_id: tagId
        }));

        const { error: insertTagsError } = await (supabase as any)
          .from('macro_tags')
          .insert(tagData) as any;

        if (insertTagsError) throw insertTagsError;
      }

      toast.success("Macro updated successfully");
      navigate("/dashboard");
    } catch (error: any) {
      toast.error(error.message || "Failed to update macro");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p className="text-muted-foreground">Loading macro...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <div className="container mx-auto px-4 py-12 max-w-3xl">
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <h1 className="text-4xl font-bold mb-3">Edit Macro</h1>
          <p className="text-lg text-muted-foreground">
            Update your macro details
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
              <CardDescription>
                Update your macro's name and description
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Macro Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Trial Balance Formatter"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="shortDesc">Short Description *</Label>
                <Input
                  id="shortDesc"
                  value={shortDesc}
                  onChange={(e) => setShortDesc(e.target.value)}
                  placeholder="e.g., Automatically formats raw trial balance exports for reporting"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Categories (optional)</Label>
                <p className="text-sm text-muted-foreground">Choose up to 2 categories that fit your macro</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {tags.map((tag) => {
                    const isSelected = selectedTags.includes(tag.id);
                    const canSelect = selectedTags.length < 2 || isSelected;
                    
                    return (
                      <div
                        key={tag.id}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedTags(selectedTags.filter(id => id !== tag.id));
                          } else if (canSelect) {
                            setSelectedTags([...selectedTags, tag.id]);
                          }
                        }}
                        className={cn(
                          "flex items-center justify-center px-3 py-2 rounded-lg border text-sm font-medium transition-all cursor-pointer",
                          isSelected 
                            ? "bg-primary text-primary-foreground border-primary" 
                            : canSelect
                            ? "bg-background hover:bg-accent hover:text-accent-foreground border-border"
                            : "bg-muted text-muted-foreground border-border cursor-not-allowed opacity-50"
                        )}
                      >
                        {tag.name}
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Compatibility */}
          <Card className="border-border/50">
            <CardHeader>
              <CardTitle>Compatibility</CardTitle>
              <CardDescription>
                Where does this macro work best?
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-3">
                  <Label className="text-sm font-medium">Operating System</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="windows"
                        checked={osSupport.includes("windows")}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setOsSupport([...osSupport, "windows"]);
                          } else {
                            setOsSupport(osSupport.filter(os => os !== "windows"));
                          }
                        }}
                      />
                      <Label htmlFor="windows" className="font-normal">Windows</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="mac"
                        checked={osSupport.includes("mac")}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setOsSupport([...osSupport, "mac"]);
                          } else {
                            setOsSupport(osSupport.filter(os => os !== "mac"));
                          }
                        }}
                      />
                      <Label htmlFor="mac" className="font-normal">macOS</Label>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label className="text-sm font-medium">Excel Version</Label>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="o365"
                        checked={excelVersions.includes("O365")}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setExcelVersions([...excelVersions, "O365"]);
                          } else {
                            setExcelVersions(excelVersions.filter(v => v !== "O365"));
                          }
                        }}
                      />
                      <Label htmlFor="o365" className="font-normal">Office 365</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="2019"
                        checked={excelVersions.includes("2019")}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setExcelVersions([...excelVersions, "2019"]);
                          } else {
                            setExcelVersions(excelVersions.filter(v => v !== "2019"));
                          }
                        }}
                      />
                      <Label htmlFor="2019" className="font-normal">Excel 2019</Label>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Documentation */}

          {/* Actions */}
          <div className="flex gap-3">
            <Button 
              type="submit" 
              size="lg" 
              disabled={saving}
              className="min-w-[150px]"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? "Saving..." : "Save Changes"}
            </Button>
            <Button 
              type="button" 
              variant="ghost" 
              size="lg" 
              onClick={() => navigate("/dashboard")}
              disabled={saving}
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Edit;
